import { ReactElement, useEffect, useRef } from 'react'
import { RootState } from 'redux/rootReducer'
import { InputEvent } from 'typings/custom'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import Checkbox from 'ui/atoms/Checkbox'
import { ErrorIcon } from 'ui/atoms/Icon'
import HorizontalRule from 'ui/atoms/HorizontalRule'
import ClientOffering from 'ui/molecules/ClientOffering'
import LoginAlert from 'ui/molecules/LoginAlert'
import useFormState from 'ui/customHooks/useFormState'
import { redirectTo } from 'ui/customHooks/useHistory'
import { clientDetail, clientInfo, customComponents } from 'configureBrand'
import i18n from 'lang/i18n'
import {
  clearLoginInfoError,
  onLoginTransmitError,
  selectFirstTimelogon,
  selectIsAuthLoading,
  selectJwt
} from 'redux/loginInfo/loginInfoSlice'
import { loginSubmitted } from 'redux/loginInfo/async-thunks'
import { colors } from 'const/colors'
import { updateRegistrationState } from 'redux/registration/registrationSlice'
import { validateNoSpace } from 'helpers/fieldValidation'
import errorMessages from 'helpers/errorMessages'
import routePaths from 'const/routePaths'
import { useAnalyticsOnLoad } from 'ui/customHooks/useAnalytics'
import { fetchDynamicContent } from 'redux/dynamicContent/dynamicContentSlice'
import {
  useAppDispatch,
  useAppSelector,
  useShallowEqualSelector
} from 'redux/hooks'
import config from 'const/config'
import Superscriptify from 'ui/atoms/Superscriptify'
import { REGISTERED_TRADEMARK } from 'const/text'
import { initResetPasswordState } from 'redux/resetPassword/resetPasswordSlice'
import { Redirect } from 'react-router-dom'
import { selectAccessToken } from 'redux/authToken/authTokenSlice'
import { selectIsUserLoggedIn } from 'redux/appInfo/appInfoSlice'
import TransmitMFA, { TransmitMfaProps } from 'ui/organisms/Transmit/Transmit'
import { fetchAuthToken } from 'redux/authToken/async-thunks'
import { TransmitResponseCodes } from 'ui/organisms/Transmit/typing'
import NeedAssistance from 'ui/molecules/NeedAssistance'
import { hasStoredUserId, readStoredUserId, storeUserId } from 'helpers/storage'
import { analyticsConfig } from 'syf-js-utilities/helpers/triggerAnalyticsEvent'
import { userAccountInfo } from 'helpers/user'
import { getCorrelationID } from 'helpers/requestParams'
import { checkDisabled, checkUserIdError, getErrorText } from './helpers'
import { DisabledProps, LoginFormState } from './typings'
import {
  AlertBannersContainer,
  Button,
  ButtonSection,
  ClientOfferingContainer,
  ContentText,
  FieldContainer,
  Header,
  InputSection,
  LoginContainer,
  LoginForm,
  LoginSection,
  Span,
  SpinnerLoader,
  TextButton,
  TextField
} from './subcomponents'
import DefaultLoginHeaderCards from './DefaultLoginHeaderCards'

const { invalidUserId } = errorMessages

const initialFormState: LoginFormState = {
  userID: '',
  password: '',
  rememberMeOption: false,
  isUserIdError: false,
  userIdError: ''
}

const { ASSET_HOST } = config

const Login = (): ReactElement => {
  const { CustomLoginHeaderCards, CustomLoginAcctLookupTooltip } =
    customComponents

  const { getLoginWelcomeMessage } = clientInfo

  const loginSectionRef = useRef(null)

  const dispatch = useAppDispatch()

  const isLoading = useAppSelector(selectIsAuthLoading)
  const firstTimeLogon = useAppSelector(selectFirstTimelogon)
  const accessToken = useAppSelector(selectAccessToken)
  const isUserLoggedIn = useAppSelector(selectIsUserLoggedIn)
  const jwt = useAppSelector(selectJwt)

  const { isLoginFailure, errorMessage } = useShallowEqualSelector(
    ({ loginInfo }: RootState) => ({
      isLoginFailure: loginInfo.isLoginFailure,
      errorMessage: loginInfo.errorMessage
    })
  )

  const { state, onChange, updateState, validateField } = useFormState({
    ...initialFormState,
    rememberMeOption: hasStoredUserId(),
    userID: readStoredUserId()
  })

  const { userID, password, rememberMeOption, isUserIdError, userIdError } =
    state as LoginFormState
  const disabledProps: DisabledProps = {
    userID,
    password,
    isUserIdError,
    isLoading
  }
  const AccountLookupTooltip = CustomLoginAcctLookupTooltip
  const isDisabled = checkDisabled({ ...disabledProps })

  const isAuthenticated =
    !isLoginFailure && !isLoading && Boolean(firstTimeLogon)

  const { storeName } = clientDetail

  const onLoginSubmit = (event: InputEvent): void => {
    event.preventDefault()

    // store user's choice with remember me checkbox
    if (rememberMeOption) {
      storeUserId(userID)
    }

    dispatch(loginSubmitted({ userID, password }))
  }

  const handleFocus = (event: InputEvent): void => {
    const {
      target: { name }
    } = event
    if (checkUserIdError(name, isUserIdError)) {
      updateState({ isUserIdError: false, userIdError: '' })
    }
    if (!errorMessage) {
      return
    }
    dispatch(clearLoginInfoError())
  }

  const handleUserID = (event: InputEvent): void => {
    validateField(event)
    const {
      target: { value }
    } = event
    if (!validateNoSpace(value)) {
      updateState({ isUserIdError: true, userIdError: invalidUserId })
    }
  }

  const onTransitResponse: TransmitMfaProps['onTransmitResponse'] =
    async response => {
      if (response.status.responseCode === TransmitResponseCodes.Success) {
        dispatch(
          fetchAuthToken({ jwtToken: response.status.token, flow: 'login' })
        )
        return
      }
      // clear userID and pw from form state for all transmit errors
      const nextState = { userID: '', password: '' }
      if (rememberMeOption) {
        // do not clear user id if remember me option is checked
        nextState.userID = userID
      }
      updateState(nextState)
      dispatch(onLoginTransmitError(response.status.responseCode))
    }

  const redirectToRegistration = (event: InputEvent): void => {
    event.preventDefault()

    if (errorMessage) {
      dispatch(
        updateRegistrationState({
          error: false,
          errorMessage: ''
        })
      )
    }

    dispatch(clearLoginInfoError())
    redirectTo(routePaths.registration)
  }

  useEffect(() => {
    dispatch(fetchDynamicContent())
  }, [dispatch])

  useEffect(() => {
    // sync. storage when user unchecks remember me
    if (!rememberMeOption) {
      storeUserId('')
    }
  }, [rememberMeOption])

  /**
   * Assigning the token value for analytics
   */
  useEffect(() => {
    userAccountInfo.trackingID = getCorrelationID()
    analyticsConfig.defaults.Token = getCorrelationID()
  }, [])

  useAnalyticsOnLoad({
    name: 'start',
    pageFunction: 'login',
    subFunction: ''
  })

  if (accessToken && isUserLoggedIn) {
    return <Redirect to={routePaths.summary} />
  }

  if (isAuthenticated && firstTimeLogon === 'N') {
    return (
      <NeedAssistance>
        <TransmitMFA
          userId={userID}
          flow="login"
          onTransmitResponse={onTransitResponse}
          jwtToken={jwt}
        />
      </NeedAssistance>
    )
  }

  if (isAuthenticated && firstTimeLogon === 'U') {
    return <Redirect to={routePaths.updatePassword} />
  }
  // firstTimeLogon flag = R | C | O
  if (isAuthenticated && firstTimeLogon) {
    return <Redirect to={routePaths.firstTimeLogin} />
  }

  return (
    <HelmetProvider>
      <Helmet>
        <title>{storeName}</title>
      </Helmet>
      <LoginContainer>
        <LoginSection>
          {CustomLoginHeaderCards ? (
            <CustomLoginHeaderCards assetHost={ASSET_HOST} />
          ) : (
            <DefaultLoginHeaderCards assetHost={ASSET_HOST} />
          )}
          <LoginForm ref={loginSectionRef}>
            <Span>{i18n({ login: 'welcome' })}</Span>
            <Header>
              <Superscriptify target={REGISTERED_TRADEMARK}>
                {storeName}
              </Superscriptify>
            </Header>
            <HorizontalRule
              isTitleBorder
              topSpacing="20px"
              bottomSpacing="16px"
            />
            <ContentText>
              <Superscriptify target={REGISTERED_TRADEMARK}>
                {getLoginWelcomeMessage(i18n)}
              </Superscriptify>
            </ContentText>
            <ContentText>
              {i18n({ login: 'newAccount' })}{' '}
              <TextButton buttonType="text" onClick={redirectToRegistration}>
                {i18n({ login: 'setUpOnlineAccess' })}
              </TextButton>{' '}
              {i18n({ login: 'setUpOnlineAccessMessage' })}
            </ContentText>

            {AccountLookupTooltip ? <AccountLookupTooltip i18n={i18n} /> : null}

            {errorMessage && (
              <AlertBannersContainer>
                <LoginAlert errorMessage={errorMessage} />
              </AlertBannersContainer>
            )}

            <form noValidate>
              <InputSection>
                <FieldContainer className="test-field-wrapper">
                  <TextField
                    id="userID"
                    name="userID"
                    value={userID}
                    type="text"
                    required
                    placeholder={i18n({ label: 'userId' }) as string}
                    errorOrHelpClassName="credential_error"
                    onFocus={handleFocus}
                    onChange={onChange}
                    onBlur={handleUserID}
                    error={userIdError || getErrorText(isLoginFailure)}
                    isError={isLoginFailure}
                    isUserIdError={isUserIdError}
                    maxLength={50}
                    errorIcon={<ErrorIcon />}
                  />
                </FieldContainer>
                <TextButton
                  buttonType="text"
                  onClick={(event: InputEvent): void => {
                    event.preventDefault()
                    dispatch(clearLoginInfoError())
                    redirectTo(routePaths.recoverUserId)
                  }}
                >
                  {i18n({ login: 'recoverYourUserID' })}
                </TextButton>
                <FieldContainer>
                  <TextField
                    id="password"
                    name="password"
                    required
                    value={password}
                    onFocus={handleFocus}
                    onChange={onChange}
                    placeholder={i18n({ label: 'password' }) as string}
                    type="password"
                    isError={isLoginFailure}
                    errorOrHelpClassName="credential_error"
                    error={getErrorText(isLoginFailure)}
                    maxLength={20}
                    showMaskedOption
                    hideTextValue="Hide"
                    showTextValue="Show"
                    data-private
                  />
                </FieldContainer>
                <TextButton
                  buttonType="text"
                  onClick={(event: InputEvent): void => {
                    event.preventDefault()
                    dispatch(initResetPasswordState())
                    dispatch(clearLoginInfoError())
                    redirectTo(routePaths.resetPassword)
                  }}
                >
                  {i18n({ login: 'resetYourPassword' })}
                </TextButton>
              </InputSection>
              <FieldContainer>
                <Checkbox
                  onChange={({ target }: InputEvent): void => {
                    const { checked } = target
                    updateState({ rememberMeOption: checked })
                  }}
                  isChecked={rememberMeOption}
                  className="checkbox"
                  aria-label="Select remember option"
                >
                  {i18n({ login: 'rememberMe' })}
                </Checkbox>
              </FieldContainer>
              <HorizontalRule />
              <ButtonSection>
                <Button
                  buttonType="primary"
                  type="submit"
                  onClick={onLoginSubmit}
                  disabled={isDisabled}
                  aria-disabled={isDisabled}
                >
                  {isLoading && (
                    <SpinnerLoader size="2x" color={colors.darkGrey} />
                  )}
                  {i18n({ login: 'logIn' })}
                </Button>
              </ButtonSection>
            </form>
          </LoginForm>
        </LoginSection>
        <ClientOfferingContainer>
          <ClientOffering />
        </ClientOfferingContainer>
      </LoginContainer>
    </HelmetProvider>
  )
}

export default Login
